import { useCallback, useRef } from 'react';
import useSubmissionProgress, {
  SubmissionStatusRecord,
  SubmissionValidationResult,
  SubmitAcceptedResponse,
  SubmitErrorClassification,
} from '../../../util/customHooks/useSubmissionProgress';
import useSubmissionRecovery from '../../../util/customHooks/useSubmissionRecovery';
import {
  INVENTORY_REQUEST_ASYNC_MESSAGES,
  INVENTORY_REQUEST_DETAIL_MESSAGES,
  INVENTORY_RETURN_DETAIL_MESSAGES,
  INVENTORY_RETURN_DETAIL_UI,
  NOTIFY_TYPE,
  STALE_RECORD_MESSAGE,
} from '../../../util/constants';
import { isStaleRecordError } from '../../../util/optimisticLock';
import { hasSubmissionInFlightErrorCode } from '../../../util/submissionErrors';

type SubmitInventoryReturnResult = {
  data?: {
    submitInventoryReturn?: SubmitAcceptedResponse & { message?: string };
  };
};

export type UseInventoryReturnSubmissionOptions = {
  caseExternalId: string | null | undefined;
  submitMutation: (options: {
    variables: Record<string, unknown>;
  }) => Promise<SubmitInventoryReturnResult>;
  openSnackBar: (type: string, message: string) => void;
  refetchReturn: () => void;
  navigateToReturns: () => void;
};

export type UseInventoryReturnSubmissionResult = {
  submitReturn: (variables: Record<string, unknown>) => void;
  isSubmissionPending: boolean;
};

/**
 * Submits an Inventory Return and follows the queued submission to its outcome, showing the
 * Return screen's existing messages: success and failure are decided by the polled status, not by
 * the mutation's reply, because the reply only means the submission was queued.
 */
const useInventoryReturnSubmission = ({
  caseExternalId,
  submitMutation,
  openSnackBar,
  refetchReturn,
  navigateToReturns,
}: UseInventoryReturnSubmissionOptions): UseInventoryReturnSubmissionResult => {
  const submitVariablesRef = useRef<Record<string, unknown>>({});
  // Only a submission started in this visit navigates on success. An outcome the user comes back
  // to still gets its message, but they stay where they are.
  const startedSubmissionIdRef = useRef<string | null>(null);

  useSubmissionRecovery(caseExternalId);

  const validate = useCallback(
    async (): Promise<SubmissionValidationResult> => ({ valid: true }),
    []
  );

  const submit = useCallback(async (): Promise<SubmitAcceptedResponse> => {
    const result = await submitMutation({ variables: submitVariablesRef.current });
    const response = result?.data?.submitInventoryReturn || {};
    startedSubmissionIdRef.current = response.submissionId || null;
    return response;
  }, [submitMutation]);

  // The order and texts the screen used before submissions were queued. Submit stays available
  // after every one of them, as it did.
  const classifySubmitError = useCallback((error: unknown): SubmitErrorClassification => {
    if (isStaleRecordError(error)) {
      return { message: STALE_RECORD_MESSAGE, canRetry: true };
    }

    if (hasSubmissionInFlightErrorCode(error)) {
      return { message: INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_IN_FLIGHT, canRetry: true };
    }

    const errorMessage = (error as { message?: string })?.message?.toLowerCase() || '';
    if (errorMessage.includes(INVENTORY_REQUEST_DETAIL_MESSAGES.PERMISSION_DENIED_MATCHER)) {
      return {
        message: INVENTORY_REQUEST_DETAIL_MESSAGES.PERMISSION_DENIED_ON_HOLD,
        canRetry: true,
      };
    }

    return { message: `Error: ${error}.`, canRetry: true };
  }, []);

  const showSubmitInFlight = useCallback((): void => {
    openSnackBar(NOTIFY_TYPE.INFO, INVENTORY_REQUEST_ASYNC_MESSAGES.SUBMIT_IN_FLIGHT);
  }, [openSnackBar]);

  const showSubmitError = useCallback((): void => {
    openSnackBar(NOTIFY_TYPE.ERROR, INVENTORY_RETURN_DETAIL_MESSAGES.SUBMIT_ERROR);
  }, [openSnackBar]);

  const handleSubmitErrorClassified = useCallback(
    (classification: SubmitErrorClassification, error: unknown): void => {
      const notifyType = hasSubmissionInFlightErrorCode(error)
        ? NOTIFY_TYPE.INFO
        : NOTIFY_TYPE.ERROR;
      openSnackBar(notifyType, classification.message);
    },
    [openSnackBar]
  );

  const handleTerminalSucceeded = useCallback(
    (statusRecord: SubmissionStatusRecord): void => {
      refetchReturn();
      openSnackBar(NOTIFY_TYPE.SUCCESS, INVENTORY_RETURN_DETAIL_MESSAGES.SUBMIT_SUCCESS);

      const startedInThisVisit =
        !!statusRecord.submissionId && statusRecord.submissionId === startedSubmissionIdRef.current;
      if (!startedInThisVisit) {
        return;
      }

      startedSubmissionIdRef.current = null;
      setTimeout(navigateToReturns, INVENTORY_RETURN_DETAIL_UI.SUBMIT_SUCCESS_NAVIGATION_DELAY_MS);
    },
    [navigateToReturns, openSnackBar, refetchReturn]
  );

  const handleTerminalFailure = useCallback((): void => {
    refetchReturn();
    showSubmitError();
  }, [refetchReturn, showSubmitError]);

  const { attemptSubmit, isSubmissionPending } = useSubmissionProgress({
    caseExternalId,
    validate,
    submit,
    classifySubmitError,
    onBlocked: showSubmitInFlight,
    onUnexpectedSubmitStatus: showSubmitError,
    onSubmitErrorClassified: handleSubmitErrorClassified,
    onTerminalSucceeded: handleTerminalSucceeded,
    onTerminalRejected: handleTerminalFailure,
    onTerminalError: handleTerminalFailure,
  });

  const submitReturn = useCallback(
    (variables: Record<string, unknown>): void => {
      // Without the record's key an accepted submission cannot be followed, and the screen would
      // read as processing for the rest of the visit.
      if (!caseExternalId) {
        showSubmitError();
        return;
      }

      submitVariablesRef.current = variables;
      attemptSubmit();
    },
    [attemptSubmit, caseExternalId, showSubmitError]
  );

  return { submitReturn, isSubmissionPending };
};

export default useInventoryReturnSubmission;
